import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardActionArea,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CallIcon from '@mui/icons-material/Call';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import LocalMallOutlinedIcon from '@mui/icons-material/LocalMallOutlined';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useTranslation } from 'react-i18next';

interface IconSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (iconType: string) => void;
  currentIcon: string;
  iconCategory: 'phone' | 'cart'; // Hangi kategori ikonları gösterilecek
}

const IconSelectionModal: React.FC<IconSelectionModalProps> = ({
  open,
  onClose,
  onSave,
  currentIcon,
  iconCategory
}) => {
  const { t } = useTranslation('salesSystem');
  const [selectedIcon, setSelectedIcon] = useState(currentIcon);

  // Telefon kategorisi ikonları
  const phoneIcons = [
    { value: 'phone', label: t('ecommerceIconSelectionModal.options.phone'), icon: <PhoneIcon sx={{ fontSize: 48 }} /> },
    { value: 'headset', label: t('ecommerceIconSelectionModal.options.headset'), icon: <HeadsetMicIcon sx={{ fontSize: 48 }} /> },
    { value: 'support', label: t('ecommerceIconSelectionModal.options.support'), icon: <SupportAgentIcon sx={{ fontSize: 48 }} /> },
    { value: 'call', label: t('ecommerceIconSelectionModal.options.call'), icon: <CallIcon sx={{ fontSize: 48 }} /> },
    { value: 'contact', label: t('ecommerceIconSelectionModal.options.contact'), icon: <ContactPhoneIcon sx={{ fontSize: 48 }} /> }
  ];

  // Sepet kategorisi ikonları
  const cartIcons = [
    { value: 'cart', label: t('ecommerceIconSelectionModal.options.cart'), icon: <ShoppingCartOutlinedIcon sx={{ fontSize: 48 }} /> },
    { value: 'bag', label: t('ecommerceIconSelectionModal.options.bag'), icon: <ShoppingBagOutlinedIcon sx={{ fontSize: 48 }} /> },
    { value: 'basket', label: t('ecommerceIconSelectionModal.options.basket'), icon: <ShoppingBasketIcon sx={{ fontSize: 48 }} /> },
    { value: 'mall', label: t('ecommerceIconSelectionModal.options.mall'), icon: <LocalMallOutlinedIcon sx={{ fontSize: 48 }} /> },
    { value: 'store', label: t('ecommerceIconSelectionModal.options.store'), icon: <StorefrontIcon sx={{ fontSize: 48 }} /> }
  ];

  const icons = iconCategory === 'phone' ? phoneIcons : cartIcons;

  const handleSave = () => {
    onSave(selectedIcon);
    onClose();
  };

  const handleCancel = () => {
    setSelectedIcon(currentIcon); // Seçimi geri al
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {iconCategory === 'phone'
          ? t('ecommerceIconSelectionModal.titlePhone')
          : t('ecommerceIconSelectionModal.titleCart')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {icons.map((icon) => (
            <Grid item xs={6} sm={4} key={icon.value}>
              <Card 
                variant="outlined"
                sx={{
                  border: selectedIcon === icon.value ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  backgroundColor: selectedIcon === icon.value ? '#e3f2fd' : 'transparent',
                  transition: 'all 0.2s'
                }}
              >
                <CardActionArea 
                  onClick={() => setSelectedIcon(icon.value)}
                  sx={{ p: 3 }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    gap: 1
                  }}>
                    {icon.icon}
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {icon.label}
                    </Typography>
                    <Radio
                      checked={selectedIcon === icon.value}
                      value={icon.value}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCancel} color="inherit">
          {t('ecommerceIconSelectionModal.actions.cancel')}
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {t('ecommerceIconSelectionModal.actions.saveSelection')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IconSelectionModal;

